import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { uploadFile } from "@/api/admin/uploadFile";
import { DEFAULT_ADMIN_FILE_INPUT_ACCEPT } from "@/constants/fileUpload";
import { Cross2Icon } from "@radix-ui/react-icons";
import type { BaseFieldProps } from "./types";

/** Admin FILE field limits (FieldRenderer / FileField only). */
const MAX_IMAGE_UPLOAD_BYTES = 200 * 1024; // 200 KB
const MAX_VIDEO_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB
const MAX_NON_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024; // 2 MB

const IMAGE_EXTENSION_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const VIDEO_EXTENSION_RE = /\.(mp4|webm|ogg|mov)$/i;

function fileIsTreatedAsImage(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  return IMAGE_EXTENSION_RE.test(file.name);
}

function fileIsTreatedAsVideo(file: File): boolean {
  if (file.type.startsWith("video/")) return true;
  return VIDEO_EXTENSION_RE.test(file.name);
}

interface FileFieldProps extends BaseFieldProps {
  description?: string;
  toast: any;
  /** Overrides `<input accept>`; defaults to admin allow list (images, PDF, video). */
  accept?: string;
  /** Outer wrapper classes; default preserves legacy min width for admin forms. */
  wrapperClassName?: string;
}

const FileField: React.FC<FileFieldProps> = ({
  fieldName,
  label,
  placeholder,
  description,
  disabled,
  className,
  required,
  hasError,
  validationMessage,
  formData,
  handleChange,
  toast,
  accept = DEFAULT_ADMIN_FILE_INPUT_ACCEPT,
  wrapperClassName,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [fileDimensions, setFileDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const getImageDimensions = (
    url: string
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      
      img.onerror = () => {
        // Silently fail for CORS or network errors
        reject(new Error("Failed to load image dimensions"));
      };
      
      // Load image from CDN/S3 URL
      img.src = url;
    });
  };

  const isImageFile = (url: string) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  const isVideoFile = (url: string) => {
    return /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  const isPdfFile = (url: string) => {
    return /\.pdf$/i.test(url);
  };

  // Reset file input to allow re-selecting the same file
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Reset file input when field value becomes empty (e.g., widget switch)
  const prevFileValueRef = useRef<string | undefined>(formData[fieldName]);

  useEffect(() => {
    const currentValue = formData[fieldName];
    // Only reset if the value actually changed from non-empty to empty
    if (currentValue !== prevFileValueRef.current) {
      prevFileValueRef.current = currentValue;
      if (!currentValue || currentValue === "") {
        // Field value is empty - reset the file input to clear browser's filename display
        resetFileInput();
        setFileDimensions(null);
      }
    }
  }, [formData, fieldName]);

  // Load dimensions for existing images on component mount and when URL changes
  useEffect(() => {
    const currentValue = formData[fieldName];
    
    if (currentValue && isImageFile(currentValue)) {
      getImageDimensions(currentValue)
        .then((dimensions) => {
          setFileDimensions(dimensions);
        })
        .catch(() => {
          // Silently fail - CORS or network error
          // Image will still display, just no dimensions shown
          setFileDimensions(null);
        });
    } else {
      setFileDimensions(null);
    }
  }, [formData[fieldName]]); // Re-run whenever the image URL changes

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = fileIsTreatedAsImage(file);
    const isVideo = fileIsTreatedAsVideo(file);


    const maxBytes = isImage
      ? MAX_IMAGE_UPLOAD_BYTES
      : isVideo ? MAX_VIDEO_UPLOAD_BYTES
      : MAX_NON_IMAGE_UPLOAD_BYTES;
    if (file.size > maxBytes) {
      const sizeLabel =
        file.size >= 1024 * 1024
          ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
          : `${(file.size / 1024).toFixed(1)} KB`;
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: isImage
          ? `Images must be 200 KB or smaller (this file is ${sizeLabel}).`
          : isVideo ? `Videos must be 10 MB or smaller (this file is ${sizeLabel}).`
          : `Files must be 2 MB or smaller (this file is ${sizeLabel}).`,
      });
      resetFileInput();
      return;
    }

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadFile(file, toast);
      if (uploadedUrl) {
        handleChange(fieldName, uploadedUrl);

        // Get image dimensions if it's an image file
        if (file.type.startsWith("image/")) {
          try {
            const dimensions = await getImageDimensions(uploadedUrl);
            setFileDimensions(dimensions);
          } catch {
            // Silently fail - CORS or network error
            // Image will still display and be usable
            setFileDimensions(null);
          }
        } else {
          setFileDimensions(null);
        }
      } else {
        // Upload returned no URL - reset the input
        resetFileInput();
      }
    } catch (error) {
      console.error("File upload error:", error);
      // Reset the file input on error so user can try again with same file
      resetFileInput();
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = () => {
    // null = explicit clear; "" matched "optional unset" and broke admin preview merge with persisted URLs
    handleChange(fieldName, null);
    setFileDimensions(null);
    resetFileInput();
  };

  return (
    <div
      className={
        wrapperClassName ?? "flex-1 min-w-80 max-w-md mb-4"
      }
    >
      {label && (
        <Label
          htmlFor={fieldName}
          className="block text-body-3-semibold font-semibold text-black mb-2"
        >
          {label}
          {required && <span className="text-secondary-main ml-1">*</span>}
        </Label>
      )}
      {description && (
        <p className="text-body-4-medium text-gray-600 mb-2">{description}</p>
      )}
      <div className="relative">
        <Input
          ref={fileInputRef}
          type="file"
          id={fieldName}
          onChange={handleFileChange}
          disabled={disabled || isUploading}
          className={`${className || ""} ${hasError ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main" : ""} file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-main file:text-white hover:file:bg-primary-main/90`}
          accept={accept}
        />
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <div className="text-body-4-medium text-gray-5">Uploading...</div>
          </div>
        )}
      </div>
      {formData[fieldName] && (
        <div className="mt-2 space-y-1">
          {/* Show Video preview */}
          {isVideoFile(formData[fieldName]) && (
            <div className="relative">
              <video
                src={formData[fieldName]}
                controls
                className="w-full max-w-[300px] rounded-md border border-gray-200"
              >
                Your browser does not support the video tag.
              </video>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteImage();
                }}
                className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors"
                title="Delete video"
              >
                <Cross2Icon className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Show PDF preview */}
          {isPdfFile(formData[fieldName]) && (
            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-md border border-gray-200">
              <svg
                className="w-8 h-8 text-red-500"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9h5v2h-5v-2zm0 3h5v2h-5v-2zm-3-3h2v2H7v-2zm0 3h2v2H7v-2z" />
              </svg>
              <div className="flex-1">
                <a
                  href={formData[fieldName]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-main hover:underline text-body-4-medium block truncate max-w-[200px]"
                >
                  {formData[fieldName].split("/").pop() || "View PDF"}
                </a>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteImage();
                }}
                className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors"
                title="Delete file"
              >
                <Cross2Icon className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Show other file types */}
          {!isImageFile(formData[fieldName]) &&
            !isVideoFile(formData[fieldName]) &&
            !isPdfFile(formData[fieldName]) && (
              <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-md border border-gray-200">
                <svg
                  className="w-8 h-8 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4z" />
                </svg>
                <div className="flex-1">
                  <a
                    href={formData[fieldName]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-main hover:underline text-body-4-medium block truncate max-w-[200px]"
                  >
                    {formData[fieldName].split("/").pop() || "View File"}
                  </a>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteImage();
                  }}
                  className="w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors"
                  title="Delete file"
                >
                  <Cross2Icon className="w-3 h-3" />
                </button>
              </div>
            )}

          {isImageFile(formData[fieldName]) && (
            <div className="mt-2 relative inline-block">
              <div className="relative w-20 h-20 rounded-md border border-gray-3 shadow-sm overflow-hidden bg-gray-1">
                <a
                  href={formData[fieldName]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={formData[fieldName]}
                    alt="Uploaded file preview"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.error("Failed to load image:", formData[fieldName]);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log("Image loaded successfully:", formData[fieldName]);
                    }}
                  />
                </a>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteImage();
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors"
                title="Delete image"
              >
                <Cross2Icon className="w-3 h-3" />
              </button>
            </div>
          )}

          {fileDimensions && (
            <p className="text-body-4-medium text-gray-5">
              Dimensions: {fileDimensions.width} × {fileDimensions.height} pixels
            </p>
          )}
        </div>
      )}
      {hasError && (
        <p className="text-secondary-main text-body-4-medium mt-1">
          {validationMessage || "This field is required"}
        </p>
      )}
    </div>
  );
};

export default FileField;


